import React, { useState, useEffect } from 'react';
import { IVaccination } from '@/types/vaccination';
import VaccinationCard from './VaccinationCard';
import MTabs from '@/components/ui/molecules/MTabs';
import MEmptyCard from '@/components/ui/organisms/MEmptyCard';
import { IconVaccineOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface VaccinationTabsProps {
  vaccinations: IVaccination[];
  onEdit: (vaccination: IVaccination) => void;
  onMarkAsDone: (vaccination: IVaccination) => void;
  onDelete: (vaccination: IVaccination) => void;
}

export const VaccinationTabs: React.FC<VaccinationTabsProps> = ({
  vaccinations,
  onEdit,
  onMarkAsDone,
  onDelete,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'pending' | 'taken'>('pending');
  const [pendingVaccinations, setPendingVaccinations] = useState<IVaccination[]>([]);
  const [takenVaccinations, setTakenVaccinations] = useState<IVaccination[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    // Parse the URL parameters
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    if (vaccinations) {
      // Sort pending by dueDate (ascending - earliest first)
      const pending = [...vaccinations]
        .filter(v => !v.taken)
        .sort((a, b) => {
          const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
          const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
          return dateA - dateB; // Sort by dueDate ascending
        });

      // Sort taken by takenOn (descending - most recent first), then by updatedAt (newest first)
      const taken = [...vaccinations]
        .filter(v => v.taken)
        .sort((a, b) => {
          // First sort by takenOn (most recent first)
          const takenOnA = a.takenOn ? new Date(a.takenOn).getTime() : 0;
          const takenOnB = b.takenOn ? new Date(b.takenOn).getTime() : 0;
          
          if (takenOnA !== takenOnB) {
            return takenOnB - takenOnA;
          }
          
          // If takenOn is the same, sort by updatedAt (newest first)
          const updatedAtA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const updatedAtB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return updatedAtB - updatedAtA;
        });

      setPendingVaccinations(pending);
      setTakenVaccinations(taken);

      // Set active tab based on URL parameter if valid, otherwise use default logic
      if (tabParam === 'taken') {
        setActiveTab('taken');
      } else if (tabParam === 'pending') {
        setActiveTab('pending');
      } else {
        setActiveTab('pending');
      }
    }
  }, [vaccinations, location.search]);

  return (
    <div className="w-full">
      <div className="profile-flex-col-gap">
        <MTabs
          activeTab={activeTab}
          tabs={[
            { value: 'pending', label: t('vaccinations.pending') },
            { value: 'taken', label: t('vaccinations.taken') }
          ]}
          onTabChange={setActiveTab}
        />

        <div className={activeTab === 'pending' ? 'mt-6' : 'mt-4'}>
          {activeTab === 'pending' && (
            <>
              {pendingVaccinations.length > 0 ? (
                <div className="space-y-4">
                  {pendingVaccinations.map((vaccination) => (
                    <VaccinationCard
                      key={vaccination.uid}
                      vaccination={vaccination}
                      onEdit={onEdit}
                      onMarkAsDone={onMarkAsDone}
                      onDelete={onDelete}
                    />
                  ))}
                </div>
              ) : (
                <MEmptyCard
                  icon={<IconVaccineOff />}
                  primaryTitle={t('vaccinations.no-vaccines')}
                  primaryCTA={{
                    text: t('vaccinations.add'),
                    onClick: () => navigate('/profile/health-profile/vaccination-tracker/add-vaccine'),
                  }}
                />
              )}
            </>
          )}

          {activeTab === 'taken' && (
            <>
              {takenVaccinations.length > 0 ? (
                <div className="space-y-4">
                  {takenVaccinations.map((vaccination) => (
                    <VaccinationCard
                      key={vaccination.uid}
                      vaccination={vaccination}
                      onEdit={onEdit}
                      onMarkAsDone={onMarkAsDone}
                      onDelete={onDelete}
                    />
                  ))}
                </div>
              ) : (
                <MEmptyCard
                  icon={<IconVaccineOff />}
                  primaryTitle= {t('vaccinations.noCompletedVaccinations')}
                  primaryCTA={{
                    text: t('vaccinations.add'),
                    onClick: () => navigate('/profile/health-profile/vaccination-tracker/add-vaccine'),
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VaccinationTabs;
